/**
 * Automation data access: TanStack Query hooks over the typed client. Every write goes through
 * the module's action endpoints and returns the action result (message, data, links).
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/lib/api/client';
import { useDebouncedValue } from '@/lib/hooks';
import type { ActionOut, MessageTemplatePut } from './types';

export const automationKeys = {
  all: ['automation'] as const,
  flows: (p: object) => ['automation', 'flows', p] as const,
  flow: (id: string) => ['automation', 'flow', id] as const,
  catalogue: ['automation', 'catalogue'] as const,
  templates: ['automation', 'templates'] as const,
  runs: (p: object) => ['automation', 'runs', p] as const,
  run: (id: string) => ['automation', 'run', id] as const,
  tasks: (p: object) => ['automation', 'tasks', p] as const,
  task: (id: string) => ['automation', 'task', id] as const,
  assignees: (q: string) => ['automation', 'assignees', q] as const,
  validate: (body: string) => ['automation', 'validate', body] as const,
  messages: ['comms', 'templates'] as const,
};

// ---------------------------------------------------------------- flows

export interface FlowQuery {
  q?: string;
  enabled?: boolean;
  trigger?: string;
}

export function useFlows(p: FlowQuery = {}, enabled = true) {
  return useQuery({
    queryKey: automationKeys.flows(p),
    queryFn: () => unwrap(api.GET('/api/automation/flows', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useFlow(id: string | undefined) {
  return useQuery({
    queryKey: automationKeys.flow(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/automation/flows/{flow_id}', { params: { path: { flow_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export function useCatalogue(enabled = true) {
  return useQuery({
    queryKey: automationKeys.catalogue,
    queryFn: () => unwrap(api.GET('/api/automation/catalogue')),
    staleTime: 10 * 60_000,
    enabled,
  });
}

export function useFlowTemplates(enabled = true) {
  return useQuery({
    queryKey: automationKeys.templates,
    queryFn: () => unwrap(api.GET('/api/automation/templates')),
    staleTime: 10 * 60_000,
    enabled,
  });
}

/** Auto-video templates rendered with chosen parameters (media's own endpoint). */
export function fetchMediaTemplates(params: { min_g?: number; seconds?: number; channels?: string[]; cooldown_s?: number }) {
  return unwrap(api.GET('/api/media/flow-templates', { params: { query: params } }));
}

const asAction = (r: unknown) => r as ActionOut;

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: automationKeys.all });
}

export interface FlowBody {
  name: string;
  description: string;
  definition: Record<string, unknown>;
  template_key?: string | null;
  kind: string;
}

export function useCreateFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: FlowBody) => asAction(await unwrap(api.POST('/api/automation/flows', { body }))),
    onSuccess: invalidate,
  });
}

export function useUpdateFlow(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: { name?: string; description?: string; definition?: Record<string, unknown>; version: number }) =>
      asAction(await unwrap(api.PATCH('/api/automation/flows/{flow_id}', { params: { path: { flow_id: id } }, body }))),
    onSuccess: invalidate,
  });
}

export function useSetFlowEnabled() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      asAction(
        await unwrap(
          enabled
            ? api.POST('/api/automation/flows/{flow_id}/enable', { params: { path: { flow_id: id } } })
            : api.POST('/api/automation/flows/{flow_id}/disable', { params: { path: { flow_id: id } } }),
        ),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => asAction(await unwrap(api.DELETE('/api/automation/flows/{flow_id}', { params: { path: { flow_id: id } } }))),
    onSuccess: invalidate,
  });
}

export function useRunFlow(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: { subject_type?: string | null; subject_id?: string | null; payload?: Record<string, unknown> }) =>
      asAction(await unwrap(api.POST('/api/automation/flows/{flow_id}/run', { params: { path: { flow_id: id } }, body }))),
    onSuccess: invalidate,
  });
}

/** Server validation of a definition, debounced while the author types. */
export function useValidation(definition: Record<string, unknown> | null) {
  const body = useDebouncedValue(definition ? JSON.stringify(definition) : '', 500);
  return useQuery({
    queryKey: automationKeys.validate(body),
    queryFn: () => unwrap(api.POST('/api/automation/validate', { body: { definition: JSON.parse(body) as Record<string, unknown>, payload: {} } })),
    enabled: !!body,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useDryRun() {
  return useMutation({
    mutationFn: (body: { definition: Record<string, unknown>; payload: Record<string, unknown> }) => unwrap(api.POST('/api/automation/test', { body })),
  });
}

export function useDraftFlow() {
  return useMutation({
    mutationFn: (request: string) => unwrap(api.POST('/api/automation/draft', { body: { request } })),
  });
}

// ---------------------------------------------------------------- runs

export interface RunQuery {
  flow?: string;
  status?: string;
  subject?: string;
  since?: string;
}

const RUN_PAGE = 50;

export function useRuns(p: RunQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: automationKeys.runs(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/automation/runs', { params: { query: { ...p, before: pageParam, limit: RUN_PAGE } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length === RUN_PAGE ? last[last.length - 1]?.started_at : undefined),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRun(id: string | undefined) {
  return useQuery({
    queryKey: automationKeys.run(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/automation/runs/{run_id}', { params: { path: { run_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export function useCancelRun() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      asAction(await unwrap(api.POST('/api/automation/runs/{run_id}/cancel', { params: { path: { run_id: id } }, body: { reason } }))),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- tasks

export interface TaskQuery {
  status?: string;
  kind?: 'task' | 'approval';
  assigned?: 'me' | 'others' | 'anyone';
}

export function useTasks(p: TaskQuery, enabled = true) {
  return useQuery({
    queryKey: automationKeys.tasks(p),
    queryFn: () => unwrap(api.GET('/api/automation/tasks', { params: { query: { ...p, limit: 300 } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: automationKeys.task(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/automation/tasks/{task_id}', { params: { path: { task_id: id ?? '' } } })),
    enabled: !!id,
  });
}

export function useCompleteTask() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, outcome, note }: { id: string; outcome: 'done' | 'approved' | 'rejected'; note: string }) =>
      asAction(await unwrap(api.POST('/api/automation/tasks/{task_id}/complete', { params: { path: { task_id: id } }, body: { outcome, note } }))),
    onSuccess: invalidate,
  });
}

export function useAssignTask() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string | null }) =>
      asAction(await unwrap(api.POST('/api/automation/tasks/{task_id}/assign', { params: { path: { task_id: id } }, body: { assignee_user_id: userId } }))),
    onSuccess: invalidate,
  });
}

export function useAssignees(q: string, enabled = true) {
  return useQuery({
    queryKey: automationKeys.assignees(q),
    queryFn: () => unwrap(api.GET('/api/automation/assignees', { params: { query: { q: q || undefined, limit: 100 } } })),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    enabled,
  });
}

// ---------------------------------------------------------------- message templates (comms)

export function useMessageTemplates(enabled = true) {
  return useQuery({ queryKey: automationKeys.messages, queryFn: () => unwrap(api.GET('/api/comms/templates')), staleTime: 60_000, enabled });
}

export function useSaveMessageTemplate(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MessageTemplatePut) => asAction(await unwrap(api.PUT('/api/comms/templates/{key}', { params: { path: { key } }, body }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: automationKeys.messages }),
  });
}

export function useResetMessageTemplate(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { channel: string; language: string }) =>
      asAction(await unwrap(api.DELETE('/api/comms/templates/{key}', { params: { path: { key }, query: p } }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: automationKeys.messages }),
  });
}

/** Assets (places) for target pickers, searched on the server. */
export function useAssetOptions(q: string, enabled = true) {
  return useQuery({
    queryKey: ['places', 'assets', 'options', q],
    queryFn: () => unwrap(api.GET('/api/places/assets', { params: { query: { q: q || undefined, limit: 100 } } })),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled,
  });
}
